// ---------------------------------------------------------------------------
// One AI opponent's profile, at /m/:provider/:name.
//
// A model is a player, so this is the account profile with a different subject
// behind it: the same `BestOf`, the same `Distributions`, out of the same
// queries. Anything that reads differently here should be because a model IS
// different, never because the page was written twice.
//
// Two things are genuinely different, and both come from the same fact.
//
// **A lobby can seat one model twice.** They are two separate players — two
// boards to answer, two scores, two places in the standing — so the header
// carries games and appearances as separate numbers whenever they disagree, and
// the recent list is one row per SEAT rather than per game. A game a model
// played against itself appears twice, under the two names it played under,
// which is the more interesting half of what happened rather than a duplicate to
// collapse.
//
// **A model can be retired.** The pinned catalogue is curated and things leave
// it. The id is the identity and the label is only decoration, so a retired
// model keeps its whole record and gains a tag saying the lobby no longer offers
// it.
//
// There is no privacy question here that the game record has not already
// answered: everything on this page is the bot's own play, plus opponents'
// scores that the public record already carries. No opponent is named.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ModelProfileResponse, ModelSeatView } from '@countdown/shared';

import { AppHeader } from '../components/layout/AppHeader.js';
import { Card, CardTitle } from '../components/primitives/Card.js';
import { getModelProfile } from '../lib/api.js';
import { gameRecordPath, modelName, ordinal } from '../lib/format.js';
import { BestOf } from './profile/BestOf.js';
import { Distributions } from './profile/Distributions.js';

type Load =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; profile: ModelProfileResponse };

function isMissing(error: Error): boolean {
  return /no such model/i.test(error.message) || /\(404\)/.test(error.message);
}

function shortDate(at: number): string {
  return new Date(at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ModelProfileScreen() {
  const { provider, name } = useParams<{ provider: string; name: string }>();
  const modelId = provider && name ? `${provider}/${name}` : null;
  /** Tagged with the id it answers, so a stale reply cannot be shown. */
  const [answer, setAnswer] = useState<{ modelId: string; load: Load } | null>(null);

  useEffect(() => {
    if (modelId === null) return;
    let live = true;

    getModelProfile(modelId)
      .then((profile) => {
        if (live) setAnswer({ modelId, load: { status: 'ready', profile } });
      })
      .catch((error: Error) => {
        if (!live) return;
        setAnswer({
          modelId,
          load: isMissing(error)
            ? { status: 'missing' }
            : { status: 'failed', message: error.message },
        });
      });

    return () => {
      live = false;
    };
  }, [modelId]);

  const load: Load =
    modelId === null
      ? { status: 'missing' }
      : answer?.modelId === modelId
        ? answer.load
        : { status: 'loading' };

  return (
    <>
      <AppHeader />
      <main className="mx-auto w-full max-w-[720px] px-4 py-6 sm:px-6">
        {load.status === 'loading' && (
          <p className="py-16 text-center text-ink-dim" aria-live="polite">
            Loading this opponent.
          </p>
        )}

        {load.status === 'missing' && (
          <div className="py-16 text-center">
            <h1 className="text-2xl font-bold">No opponent by that name</h1>
            <p className="mx-auto mt-3 max-w-[400px] text-ink-muted text-pretty">
              This link names a model the site has never seated and does not
              offer. Models are pinned to an exact version, so a link written for
              an older one can end up here.
            </p>
            <Link
              to="/models"
              className="mt-6 inline-block min-h-11 leading-11 text-blue-200 underline"
            >
              Every AI opponent
            </Link>
          </div>
        )}

        {load.status === 'failed' && (
          <div className="py-16 text-center">
            <h1 className="text-2xl font-bold">That opponent did not load</h1>
            <p className="mt-3 text-ink-muted">{load.message}</p>
            <Link to="/" className="mt-6 inline-block min-h-11 leading-11 text-blue-200 underline">
              Back to the home page
            </Link>
          </div>
        )}

        {load.status === 'ready' && <ModelProfile profile={load.profile} />}
      </main>
    </>
  );
}

/**
 * The profile itself, without the fetch around it.
 *
 * Exported so the component gallery can mount one from a fixture, the same way
 * `GameRecord` is.
 */
export function ModelProfile({ profile }: { profile: ModelProfileResponse }) {
  const { stats, recentSeats } = profile;
  const played = stats.seatsHeld > 0;

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-3xl font-bold">{modelName(profile.modelId, profile.label)}</h1>
          {/* The full id in the boxed mono slot, the same treatment a username
              gets. It is the identity: the label is whatever the catalogue calls
              it today, and two pinned versions of one model are two players. */}
          <span className="rounded-chip border border-line bg-surface px-2 py-1 font-mono text-xs text-ink-muted">
            {profile.modelId}
          </span>
          {profile.retired ? (
            <span className="rounded-chip border border-line bg-surface px-2 py-1 font-mono text-[11px] tracking-[0.1em] uppercase text-ink-dim">
              retired
            </span>
          ) : null}
        </div>
        {profile.note ? <p className="text-sm text-ink-muted">{profile.note}</p> : null}
        <p className="text-sm text-ink-dim">
          {played
            ? `Played here since ${shortDate(profile.firstSeenAt ?? 0)}${
                profile.retired ? '. No longer offered in the lobby.' : '.'
              }`
            : profile.retired
              ? 'Never seated, and no longer offered in the lobby.'
              : 'Offered in the lobby, and has not played a finished game yet.'}
        </p>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Games, with appearances as the note rather than as a fifth tile.
            They differ only when a lobby seated this model more than once, and
            two headline numbers that are usually equal is a question raised
            rather than answered. The distributions card carries the same pair
            for whichever corpus is on screen. */}
        <Stat
          label="games"
          value={`${stats.gamesPlayed}`}
          note={
            stats.seatsHeld > stats.gamesPlayed
              ? `${stats.seatsHeld} appearances`
              : undefined
          }
        />
        <Stat label="wins" value={`${stats.wins}`} />
        <Stat
          label="points per round"
          value={stats.pointsPerRound === null ? '—' : `${stats.pointsPerRound}`}
          note={`over ${stats.roundsPlayed} rounds`}
        />
        <Stat label="conundrums solved" value={`${stats.conundrumsSolved}`} />
      </div>

      <BestOf stats={stats} />

      <ConundrumsItKnew shadow={stats.conundrumShadow} solved={stats.conundrumsSolved} />

      <Distributions distributions={profile.distributions} subject="model" />

      <Card>
        <CardTitle>Recent seats</CardTitle>
        {recentSeats.length === 0 ? (
          <p className="mt-3 text-sm text-ink-dim">
            No finished games yet. Games count once they reach the end.
          </p>
        ) : (
          <>
            <p className="mt-1 text-xs text-ink-dim">
              One row per seat. A lobby that sat this model down twice shows twice.
            </p>
            <ul className="mt-3 flex flex-col divide-y divide-line">
              {recentSeats.map((seat) => (
                <SeatRow key={`${seat.gameId}-${seat.displayName}-${seat.placement}`} seat={seat} />
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * What this model does with a conundrum, as opposed to what it wins.
 *
 * **`conundrums solved` is a race and this is not.** A conundrum hands its
 * points to exactly one seat — `buzz()` refuses the second buzzer outright — so
 * a model in a room of four is zero for the round however fast it was, and the
 * headline tile above says so. Every model still produced an answer, and until
 * now the game deleted it: Isaac's point was that it is compute already bought,
 * and "this model gets the conundrum nine times in ten" is a real fact the round
 * was destroying.
 *
 * **Two rates, and the page prints both because they answer different
 * questions.** The solve rate is over answers that arrived while the round was
 * still open, which is the only population where "could have won it" means
 * anything. The vocabulary rate is over every answer, including ones that took
 * two minutes — kept deliberately, and never silently folded into the first.
 * When the two are equal only the first is drawn, because the same figure under
 * two headings reads as two claims.
 */
function ConundrumsItKnew({
  shadow,
  solved,
}: {
  shadow: ModelProfileResponse['stats']['conundrumShadow'];
  solved: number;
}) {
  // No block at all rather than a card of dashes. A model nobody has asked a
  // conundrum has nothing to say here, and every row would be `0 of 0`.
  if (shadow === null || shadow.attempts === 0) return null;

  const rate = (n: number, of: number) => (of === 0 ? '—' : `${Math.round((n / of) * 100)}%`);
  const lateOnes = shadow.attempts - shadow.inTime;

  return (
    <Card>
      <CardTitle>Conundrums it knew</CardTitle>
      <p className="mt-1 text-xs text-ink-dim">
        A conundrum takes one buzz, so most answers a model works out are never played.
        These are all of them.
      </p>
      <dl className="mt-3 flex flex-col gap-4">
        <div>
          <dt className="label-mono">solved, in time</dt>
          <dd className="mt-1.5 flex flex-wrap items-baseline gap-2">
            <span className="tabular font-mono text-xl font-semibold">
              {shadow.inTimeCorrect} of {shadow.inTime}
            </span>
            <span className="text-sm text-ink-muted">
              {rate(shadow.inTimeCorrect, shadow.inTime)}, and {solved} of them scored
            </span>
          </dd>
        </div>
        {shadow.correct !== shadow.inTimeCorrect ? (
          <div>
            <dt className="label-mono">right at any speed</dt>
            <dd className="mt-1.5 flex flex-wrap items-baseline gap-2">
              <span className="tabular font-mono text-xl font-semibold">
                {shadow.correct} of {shadow.attempts}
              </span>
              <span className="text-sm text-ink-muted">
                {rate(shadow.correct, shadow.attempts)}, counting {lateOnes} that landed after
                the round
              </span>
            </dd>
          </div>
        ) : null}
        {shadow.medianLatencyMs === null ? null : (
          <div>
            <dt className="label-mono">typical time to answer</dt>
            <dd className="mt-1.5 flex flex-wrap items-baseline gap-2">
              <span className="tabular font-mono text-xl font-semibold">
                {(shadow.medianLatencyMs / 1000).toFixed(1)}s
              </span>
              {/* The middle answer, not the mean: one two-minute reply drags an
                  average somewhere that describes none of the others. */}
              <span className="text-sm text-ink-muted">the middle one of {shadow.attempts}</span>
            </dd>
          </div>
        )}
      </dl>
    </Card>
  );
}

function SeatRow({ seat }: { seat: ModelSeatView }) {
  return (
    <li>
      <Link
        to={gameRecordPath(seat.gameId)}
        className="flex items-center justify-between gap-3 py-3 hover:bg-surface"
      >
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-x-2 font-medium">
            <span className="truncate">{seat.displayName}</span>
            <span className="text-ink-muted">
              {ordinal(seat.placement)} of {seat.playerCount}
            </span>
            {seat.seatsForModel > 1 ? (
              // It played the game against a copy of itself, which is a fact
              // about the game rather than about this seat, so it is a quiet tag
              // rather than a headline.
              <span
                className="rounded-chip border border-line px-1.5 py-0.5 font-mono text-[10px] tracking-[0.08em] uppercase text-ink-dim"
                data-self-match={seat.seatsForModel}
              >
                {seat.seatsForModel} seats
              </span>
            ) : null}
          </p>
          <p className="text-xs text-ink-dim">
            {shortDate(seat.at)}
            {seat.difficulty ? ` · ${seat.difficulty}` : ''}
          </p>
        </div>
        <p className="tabular font-mono text-lg font-semibold">{seat.score}</p>
      </Link>
    </li>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rounded-card border border-line bg-surface px-3 py-3">
      <p className="label-mono">{label}</p>
      <p className="tabular mt-1 text-2xl font-bold">{value}</p>
      {note ? <p className="mt-0.5 text-xs text-ink-dim">{note}</p> : null}
    </div>
  );
}
